import {useState, useEffect} from 'react';
import {OverlayTrigger, Button, Popover, Tooltip} from '@goproblems/ui';

import {AnimatedHearts} from '@components';
import {
    AnonymousUser,
    Tournament,
    TournamentPlayerStatus,
    TournamentRound,
    User,
    UserMinimal,
} from '@types';
import {
    roundIsOngoing,
    tournamentIsFinished,
    tournamentIsRunning,
    tournamentIsScheduled,
    useRoundCountdown,
    useRoundElapsedTime,
    useSelector,
    useTournamentCountdown,
} from '@utils';
import {useJoinTournamentMutation, useLeaveTournamentMutation} from '@api';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router';
import {TournamentTitle, type TournamentBackLink} from './TournamentPage';
import {toast} from 'react-toastify';
dayjs.extend(advancedFormat);

const LEAD_TIME = 10 + 1; // seconds
const NOTIFICATION_INDEX_DB_NAME = 'notifications-db';

export const TournamentHeader = ({
    tournament,
    back,
    refetch,
    resultsFor,
    onRoundStart,
    onRoundEnd,
    onRoundElapsedTimeChange,
}: {
    tournament: Tournament;
    back?: TournamentBackLink;
    refetch?: () => void;
    nextRound?: TournamentRound;
    resultsFor?: User | UserMinimal | AnonymousUser;
    onRoundStart?: () => void;
    onRoundEnd?: () => void;
    onRoundElapsedTimeChange?: (elapsedTime: number) => void;
}) => {
    const config = tournament.rulesetConfiguration;
    const {user: loggedUser} = useSelector(state => state.auth);
    const [joinTournament] = useJoinTournamentMutation();
    const [leaveTournament] = useLeaveTournamentMutation();
    const [currentRoundElapsedTime] = useRoundElapsedTime(
        tournament.currentRound,
        config.roundDuration
    );
    const [nextRoundCountdown] = useRoundCountdown(
        tournament.currentRound,
        config.pauseBetweenRounds
    );
    const [startCountdown] = useTournamentCountdown(tournament);
    const scheduledCountdown = Math.max(0, startCountdown);
    const players =
        tournament.currentRound?.results.map(({player}) => player) ?? [];
    const joined = players.some(player => player.user.id === loggedUser?.id);
    const playersCount = tournament.playersCount;
    const activePlayers = players.filter(
        player => player.status === TournamentPlayerStatus.Active
    );
    const [notified, setNotified] = useState(false);

    const {t} = useTranslation();

    const currentRound = tournament.currentRound;

    const checkNotifications = async (
        tournamentId: number
    ): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(NOTIFICATION_INDEX_DB_NAME, 1);
            request.onupgradeneeded = event => {
                const db = (event.target as IDBOpenDBRequest)
                    .result as IDBDatabase;
                if (!db.objectStoreNames.contains('notifications')) {
                    db.createObjectStore('notifications', {keyPath: 'id'});
                }
            };

            request.onsuccess = event => {
                const db = (event.target as IDBOpenDBRequest)
                    .result as IDBDatabase;

                db.onversionchange = () => {
                    console.warn(
                        'Database version changed. Closing the connection.'
                    );
                    db.close();
                };
                const tx = db.transaction('notifications', 'readonly');
                const store = tx.objectStore('notifications');
                const allRequest = store.getAll();

                allRequest.onsuccess = () => {
                    const notifications = allRequest.result;
                    resolve(
                        notifications.some(
                            notification =>
                                notification.meta?.tournamentId === tournamentId
                        )
                    );

                    resolve(
                        allRequest.result.filter(
                            r => r.meta?.tournamentId === tournamentId
                        ).length > 0
                    );
                };

                request.onerror = () => {
                    reject(false);
                };
            };

            request.onerror = () => {
                reject(false);
            };
        });
    };

    const scheduleTournamentNotification = async ({
        title,
        body,
        scheduleTime,
        url,
        meta,
    }: {
        title: string;
        body: string;
        scheduleTime: number;
        url?: string | undefined;
        meta?: any;
    }) => {
        if (!('Notification' in window)) {
            alert(t('tournaments.notifications_not_supported'));
            return false;
        }

        let permission = Notification.permission;
        if (permission !== 'granted') {
            permission = await Notification.requestPermission();
        }

        if (permission !== 'granted') {
            console.warn('Notification permission denied');
            return false;
        }

        navigator.serviceWorker.ready
            .then(registration => {
                if (registration.active) {
                    registration.active.postMessage({
                        type: 'SCHEDULE_NOTIFICATION',
                        scheduleTime,
                        title,
                        body,
                        url,
                        meta,
                    });
                } else {
                    console.warn(
                        'Service Worker registration found but no active worker.'
                    );
                }
            })
            .catch(() => {
                console.warn('Service Worker not ready');
            });
        return true;
    };

    useEffect(() => {
        if (nextRoundCountdown === 0) {
            if (onRoundStart) onRoundStart();
        }
    }, [nextRoundCountdown, onRoundStart]);

    useEffect(() => {
        if (currentRoundElapsedTime >= config.roundDuration) {
            if (onRoundEnd) onRoundEnd();
        }
    }, [currentRoundElapsedTime, onRoundEnd, config.roundDuration]);

    useEffect(() => {
        if (onRoundElapsedTimeChange) {
            onRoundElapsedTimeChange(currentRoundElapsedTime);
        }
    }, [currentRoundElapsedTime, onRoundElapsedTimeChange]);

    useEffect(() => {
        void checkNotifications(tournament.id).then(hasNotifications => {
            setNotified(hasNotifications);
        });
    }, [tournament.id]);

    return (
        <header className="tournament-header">
            <div className="tournament-heading-main">
                <div className="tournament-title-row">
                    <TournamentTitle back={back}>
                        {t('tournaments.tournament')} #{tournament.id}
                    </TournamentTitle>
                    <span
                        className={`tournament-status is-${tournament.status}`}>
                        {t(
                            `tournaments.${tournament.status}`,
                            tournament.status
                        )}
                    </span>
                </div>
                <div className="tournament-meta">
                    {resultsFor ? (
                        <Link to={`/users/${resultsFor.name}`}>
                            {t('tournaments.results_for')}: {resultsFor.name}
                        </Link>
                    ) : (
                        <>
                            <Link
                                to={`/tournaments/${tournament.id}/rules`}
                                className="tournament-ruleset-link">
                                {t(
                                    `tournaments.rulesets.${tournament.ruleset}`
                                )}
                            </Link>
                            <div className="flex items-center gap-2">
                                <div className="min-w-16">
                                    <AnimatedHearts
                                        maxLives={config.lives}
                                        lives={config.lives}
                                        gap={1}
                                        size={22}
                                    />
                                </div>
                                <OverlayTrigger
                                    placement="bottom"
                                    delay={{show: 250, hide: 400}}
                                    overlay={
                                        <Popover
                                            id="rating-popover"
                                            className="p-3">
                                            {t(
                                                'tournaments.rulesets.round_duration_desc'
                                            )}
                                        </Popover>
                                    }>
                                    <div
                                        className="tournament-duration"
                                        tabIndex={0}>
                                        {config.roundDuration}s
                                    </div>
                                </OverlayTrigger>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="tournament-header-controls">
                <div className="tournament-timing">
                    {tournamentIsScheduled(tournament) && (
                        <div>
                            {scheduledCountdown > 0 && (
                                <span>
                                    {t(
                                        'tournaments.the_tournament_will_start_in'
                                    )}{' '}
                                </span>
                            )}
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id="start-time-tooltip">
                                        {t(
                                            'tournaments.the_tournament_will_start_at'
                                        )}{' '}
                                        {dayjs(tournament.startedAt).format(
                                            'YYYY-MM-DD HH:mm:ss'
                                        )}
                                    </Tooltip>
                                }>
                                <span className="ml-1 font-mono cursor-pointer">
                                    {scheduledCountdown === 0
                                        ? t('tournaments.starting_soon')
                                        : `${Math.floor(scheduledCountdown / 3600)}:${String(Math.floor((scheduledCountdown % 3600) / 60)).padStart(2, '0')}:${String(scheduledCountdown % 60).padStart(2, '0')}`}
                                </span>
                            </OverlayTrigger>
                        </div>
                    )}
                    {currentRound &&
                        tournamentIsRunning(tournament) &&
                        !roundIsOngoing(currentRound) && (
                            <div>
                                <span className="font-semibold mr-2">
                                    {t('tournaments.break_will_be_over_in')}:
                                </span>
                                {dayjs
                                    .duration(nextRoundCountdown, 'seconds')
                                    .format('m:ss')}
                            </div>
                        )}
                    {currentRound &&
                        tournamentIsRunning(tournament) &&
                        roundIsOngoing(currentRound) && (
                            <>
                                <span className="font-semibold mr-2">
                                    {t('tournaments.round')}{' '}
                                    {currentRound.number}:
                                </span>
                                <span className="font-mono">
                                    {dayjs
                                        .duration(
                                            Math.max(
                                                0,
                                                config.roundDuration -
                                                    currentRoundElapsedTime
                                            ),
                                            'seconds'
                                        )
                                        .format('m:ss')}
                                </span>
                            </>
                        )}
                    {tournamentIsFinished(tournament) && (
                        <>
                            {dayjs(tournament.startedAt).format(
                                'MMM D, YYYY · HH:mm'
                            )}
                        </>
                    )}
                </div>
                <div className="tournament-header-actions">
                    {tournamentIsScheduled(tournament) &&
                        !tournamentIsRunning(tournament) && (
                            <div>
                                {playersCount} {t('tournaments.entrants')}
                            </div>
                        )}
                    {tournamentIsRunning(tournament) && (
                        <div>
                            <span className="font-mono">
                                {activePlayers.length}/{playersCount}
                            </span>
                            <span className="ml-2">
                                {t('tournaments.remain')}
                            </span>
                        </div>
                    )}
                    <div className="tournament-join-actions">
                        {tournamentIsScheduled(tournament) &&
                            !tournamentIsRunning(tournament) &&
                            loggedUser &&
                            joined && (
                                <Button
                                    className="tournament-notify"
                                    variant="outline-secondary"
                                    disabled={notified}
                                    size="sm"
                                    onClick={async () => {
                                        const title = t(
                                            'tournaments.tournament_reminder'
                                        );
                                        try {
                                            const body = t(
                                                'tournaments.tournament_is_starting_in_x_seconds',
                                                {
                                                    // notify 1 second in advance
                                                    x: LEAD_TIME - 1,
                                                }
                                            );
                                            const scheduleTime =
                                                Date.parse(
                                                    tournament.startedAt
                                                ) -
                                                LEAD_TIME * 1000;
                                            const success =
                                                await scheduleTournamentNotification(
                                                    {
                                                        title,
                                                        body,
                                                        scheduleTime,
                                                        url: `/tournaments/${tournament.id}/lobby`,
                                                        meta: {
                                                            tournamentId:
                                                                tournament.id,
                                                        },
                                                    }
                                                );
                                            setNotified(success);
                                            if (success) {
                                                const body = t(
                                                    'tournaments.tournament_notify_success'
                                                );
                                                await scheduleTournamentNotification(
                                                    {
                                                        title,
                                                        body,
                                                        scheduleTime:
                                                            Date.now(),
                                                        meta: {
                                                            tournamentId:
                                                                tournament.id,
                                                        },
                                                    }
                                                );
                                            } else {
                                                toast.error(
                                                    t(
                                                        'tournaments.tournament_notify_failure'
                                                    )
                                                );
                                            }
                                        } catch {
                                            setNotified(false);
                                        }
                                    }}>
                                    {notified
                                        ? t('tournaments.notified')
                                        : t('tournaments.notify_me')}
                                </Button>
                            )}
                        {tournamentIsScheduled(tournament) &&
                            !tournamentIsRunning(tournament) &&
                            loggedUser &&
                            (joined ? (
                                <Button
                                    className="tournament-leave"
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={async () => {
                                        await leaveTournament({
                                            id: tournament.id,
                                        }).unwrap();
                                        if (refetch) refetch();
                                    }}>
                                    {t('tournaments.leave')}
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={async () => {
                                        await joinTournament({
                                            id: tournament.id,
                                        }).unwrap();
                                        if (refetch) refetch();
                                    }}>
                                    {t('tournaments.join')}
                                </Button>
                            ))}
                    </div>

                    {!loggedUser && tournamentIsScheduled(tournament) && (
                        <Button variant="outline-secondary" size="sm" disabled>
                            {t('tournaments.log_in_to_join')}
                        </Button>
                    )}
                </div>
            </div>
        </header>
    );
};
